import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, Download, QrCode, MessageCircle, Sparkles, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Phase 323b — Owner Dashboard Self-Signup QR Card
 * ------------------------------------------------
 * An always-on card on the Owner Dashboard. Shows the wa.me self-signup
 * QR + a one-tap "Send to my WhatsApp" button that opens WhatsApp with
 * a pre-composed message containing the share-link + QR image URL, so
 * the owner can forward it straight into their parents/staff WhatsApp
 * group.
 *
 * Optional props:
 *   role  — 'student' | 'employee' (default 'student')
 */
export default function DashboardSelfSignupQRCard({ role = 'student' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/academy/self-signup-qr?role=${role}`);
      setData(res.data);
    } catch (err) {
      // Silent fail — card disappears if endpoint errors (don't break dashboard).
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const pngUrl = (() => {
    if (typeof window === 'undefined') return '';
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/public/signup-qr.png?role=${role}`;
  })();

  const handleSendToMyWhatsApp = () => {
    if (!data?.wa_link) return;
    const caption = role === 'employee'
      ? `Join our academy as a coach/staff member — tap to open WhatsApp:\n${data.wa_link}\n\nOr scan our QR: ${pngUrl}`
      : `Register your child with our academy — tap to open WhatsApp:\n${data.wa_link}\n\nOr scan our QR: ${pngUrl}`;
    // wa.me/ (no phone) → WA asks the owner to pick a contact/group → ideal flow for forwarding into a parent group.
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  const handleCopyLink = () => {
    if (!data?.wa_link) return;
    navigator.clipboard.writeText(data.wa_link);
    toast.success('WhatsApp link copied — paste it in your parents group!');
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = pngUrl;
    a.download = role === 'employee' ? 'employee-signup-qr.png' : 'student-signup-qr.png';
    a.click();
  };

  const handleDownloadPoster = async () => {
    try {
      const res = await api.get(`/academy/self-signup-qr.pdf?role=${role}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = role === 'employee' ? 'employee-signup-poster.pdf' : 'student-signup-poster.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('A4 poster downloaded — print and stick at the gate!');
    } catch (err) {
      toast.error('Failed to generate poster PDF');
    }
  };

  if (loading) {
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-white" data-testid="dashboard-self-signup-qr-card-loading">
        <CardContent className="p-5">
          <div className="flex gap-4">
            <Skeleton className="h-32 w-32 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-9 w-40 mt-3" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.configured) {
    return null; // Card hides itself when WA isn't configured.
  }

  return (
    <Card
      className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-white overflow-hidden relative"
      data-testid="dashboard-self-signup-qr-card"
    >
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-emerald-200/40 blur-2xl pointer-events-none" />
      <CardContent className="p-5 relative">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* QR */}
          <div className="shrink-0 flex justify-center sm:justify-start">
            <div className="bg-white p-2 rounded-lg border border-emerald-200 shadow-sm">
              <img
                src={data.qr_data_url}
                alt="Student signup WhatsApp QR"
                className="w-32 h-32"
                data-testid="dashboard-self-signup-qr-image"
              />
            </div>
          </div>
          {/* Copy + CTAs */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-medium uppercase tracking-wide">
              <Sparkles className="h-3.5 w-3.5" />
              {role === 'employee' ? 'Hire Faster' : 'Auto-Onboard Students'}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight" data-testid="dashboard-self-signup-qr-title">
              {role === 'employee'
                ? 'Share this QR in your staff WhatsApp group'
                : 'Share this QR in your parents WhatsApp group'}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {role === 'employee'
                ? 'Anyone who scans gets WhatsApp pre-filled with "Start Employee Registration" — our bot collects their details and creates the staff record automatically.'
                : 'Anyone who scans gets WhatsApp pre-filled with "Start Registration" — our bot collects child name, DOB, sport, parent contact and creates the student record automatically.'}
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleSendToMyWhatsApp}
                data-testid="dashboard-self-signup-send-wa"
                className="bg-emerald-600 hover:bg-emerald-700 text-xs gap-1.5"
              >
                <MessageCircle className="h-3.5 w-3.5" /> Send to my WhatsApp
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyLink}
                data-testid="dashboard-self-signup-copy"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs gap-1.5"
              >
                <Copy className="h-3.5 w-3.5" /> Copy link
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                data-testid="dashboard-self-signup-download"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs gap-1.5"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </Button>
              <Button
                size="sm"
                onClick={handleDownloadPoster}
                data-testid="dashboard-self-signup-download-poster"
                className="bg-emerald-700 hover:bg-emerald-800 text-xs gap-1.5"
              >
                <Printer className="h-3.5 w-3.5" /> Print poster
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
              <QrCode className="h-2.5 w-2.5" /> Sends to: <span className="font-mono">+{data.display_phone}</span>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
